// src/modules/myvault/components/RecipientPicker.tsx

// What? Reusable recipient configuration UI for SEND_EMAIL actions.
// Why? Automation rules and canvas action buttons need to configure who gets emailed.
// How? A list of recipient rows, each with a type selector (Email, Group, Role Field,
//      Record Field) and a type-appropriate value input. Groups come from useGroups hook.

import React from "react";
import {
  Box,
  Button,
  FormLabel,
  IconButton,
  Option,
  Select,
  Input,
  Stack,
  Typography,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ApplicationGroup } from "../hooks/useGroups";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RecipientType = "email" | "group" | "role" | "field";

export interface RecipientEntry {
  type: RecipientType;
  /** Literal address (type=email) */
  address?: string;
  /** Group UUID (type=group) */
  group_id?: string;
  /** Column name on the trigger row (type=role or type=field) */
  field?: string;
}

interface RecipientPickerProps {
  /** Current list of recipient entries. */
  value: RecipientEntry[];
  /** Called when the list changes. */
  onChange: (value: RecipientEntry[]) => void;
  /** Available groups in this workspace. */
  groups: ApplicationGroup[];
  /** FK column names available for role resolution (e.g. manager_id). */
  roleFields?: string[];
  /** Text/email column names available for direct field lookup. */
  recordFields?: string[];
  /** Disable editing (read-only). */
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const RECIPIENT_TYPE_LABELS: Record<RecipientType, string> = {
  email: "Email Address",
  group: "Group",
  role: "Role Field (FK)",
  field: "Record Field",
};

const emptyEntry = (): RecipientEntry => ({ type: "email", address: "" });

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const RecipientPicker: React.FC<RecipientPickerProps> = ({
  value,
  onChange,
  groups,
  roleFields = [],
  recordFields = [],
  disabled = false,
}) => {
  const handleAdd = () => {
    onChange([...value, emptyEntry()]);
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleTypeChange = (index: number, newType: RecipientType) => {
    const updated = [...value];
    // Reset value fields when type changes
    updated[index] = { type: newType };
    onChange(updated);
  };

  const handleValueChange = (
    index: number,
    field: keyof RecipientEntry,
    val: string,
  ) => {
    const updated = [...value];
    updated[index] = { ...updated[index], [field]: val };
    onChange(updated);
  };

  const renderValueInput = (entry: RecipientEntry, index: number) => {
    switch (entry.type) {
      case "email":
        return (
          <Input
            size="sm"
            placeholder="user@example.com"
            value={entry.address || ""}
            onChange={(e) =>
              handleValueChange(index, "address", e.target.value)
            }
            disabled={disabled}
            sx={{ flex: 1, minWidth: 200 }}
          />
        );

      case "group":
        return (
          <Select
            size="sm"
            placeholder="Select group..."
            value={entry.group_id || ""}
            onChange={(_, val) =>
              handleValueChange(index, "group_id", val as string)
            }
            disabled={disabled}
            sx={{ flex: 1, minWidth: 200 }}
          >
            {groups.map((g) => (
              <Option key={g.id} value={g.id}>
                {g.display_name}
              </Option>
            ))}
          </Select>
        );

      case "role":
        return (
          <Select
            size="sm"
            placeholder="Select FK field..."
            value={entry.field || ""}
            onChange={(_, val) =>
              handleValueChange(index, "field", val as string)
            }
            disabled={disabled}
            sx={{ flex: 1, minWidth: 200 }}
          >
            {roleFields.map((f) => (
              <Option key={f} value={f}>
                {f}
              </Option>
            ))}
            {roleFields.length === 0 && (
              <Option value="" disabled>
                No FK columns available
              </Option>
            )}
          </Select>
        );

      case "field":
        return (
          <Select
            size="sm"
            placeholder="Select email field..."
            value={entry.field || ""}
            onChange={(_, val) =>
              handleValueChange(index, "field", val as string)
            }
            disabled={disabled}
            sx={{ flex: 1, minWidth: 200 }}
          >
            {recordFields.map((f) => (
              <Option key={f} value={f}>
                {f}
              </Option>
            ))}
            {recordFields.length === 0 && (
              <Option value="" disabled>
                No email/text columns available
              </Option>
            )}
          </Select>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <FormLabel>Recipients</FormLabel>
      <Stack spacing={1} sx={{ mt: 0.5 }}>
        {value.length === 0 && (
          <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
            No recipients configured. Add at least one.
          </Typography>
        )}

        {value.map((entry, index) => (
          <Stack
            key={index}
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              p: 1,
              borderRadius: "sm",
              bgcolor: "background.level1",
            }}
          >
            <Select
              size="sm"
              value={entry.type}
              onChange={(_, val) =>
                handleTypeChange(index, val as RecipientType)
              }
              disabled={disabled}
              sx={{ minWidth: 140 }}
            >
              {(
                Object.entries(RECIPIENT_TYPE_LABELS) as [
                  RecipientType,
                  string,
                ][]
              ).map(([key, label]) => (
                <Option key={key} value={key}>
                  {label}
                </Option>
              ))}
            </Select>

            {renderValueInput(entry, index)}

            <IconButton
              size="sm"
              variant="plain"
              color="danger"
              onClick={() => handleRemove(index)}
              disabled={disabled}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}

        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          startDecorator={<AddIcon />}
          onClick={handleAdd}
          disabled={disabled}
          sx={{ alignSelf: "flex-start" }}
        >
          Add Recipient
        </Button>
      </Stack>
    </Box>
  );
};

export default RecipientPicker;
